<script lang="ts" setup>
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import type { Home8to10 } from '~/models/Content/Home8to10'
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import type { ContentLink } from '~/models/Content/ContentLink'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import seedrandom from 'seedrandom'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import { useQuery } from '@tanstack/vue-query'
import { firstOf } from '~/utils/queryUtils'
import useArrayUtils from '~/utils/arrayUtils'
import useAuthStore from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import useTime from '~/composables/useTime'
import useTaskLocations from '~/composables/useTaskLocations'
import { useMedia } from '~/composables/useMedia'
import useHome from '~/composables/useHome'
import { useAssignTask } from '~/composables/useAssignTask'
import { EXAM_TAG, isExamTask, SHARE_TAG } from '~/composables/useAssignTags'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import Image from '~/components/media/Image.vue'
import HomeSubjects from '~/components/home/HomeSubjects.vue'
import Home8To10Shared from '~/components/home/home8to10/Home8To10Shared.vue'
import Home8To10FunFact from '~/components/home/home8to10/Home8To10FunFact.vue'
import Greeting from '~/components/home/Greeting.vue'
import DefaultMessages from '~/components/home/DefaultMessages.vue'
import BannerArticles from '~/components/home/BannerArticles.vue'

useAppColor(ColorName.Green40)

const { t } = useI18n()
const { isTeacher } = storeToRefs(useAuthStore())
const { currentWeekNumber } = useTime()
const { findContents } = useContentApi()
const { truthy } = useArrayUtils()
const { currentDate } = useTime()

const { tasks: unfilteredTasks, isLoading: isLoadingTasks } = useAssignTask({
  tags: [SHARE_TAG, EXAM_TAG],
  additionalParams: computed(() => ({ week: currentWeekNumber }))
})

const tasks = computed(() => unfilteredTasks.value?.filter(task => (!task.startDate) || task.startDate < DateTime.now()))

const { enrichedTasks, isLoadingLocations } = useTaskLocations(tasks)

const { data, isLoading: isLoadingHome } = useHome<Home8to10>(ContentType.Home8to10)

const randomSeed = seedrandom(currentDate)

const teacherRelationId = computed(() => Number(data?.value?.teacherRelation?.destinationContentId ?? 0))
const sectionIds = computed(() => data?.value?.sections?.destinationContentIds ?? [])
const funFactIds = computed(() => data?.value?.funFactsRelations?.destinationContentIds ?? [])
const funFactRandom = computed(() => funFactIds.value[Math.floor(randomSeed() * funFactIds.value.length)] ?? 0)
const relationIds = computed(() => [
  teacherRelationId.value,
  funFactRandom.value,
  ...sectionIds.value,
].filter(truthy<number>))

const { data: relations, isLoading: isLoadingRelations } = useQuery({
  enabled: computed(() => relationIds.value.length > 0),
  queryKey: computed(() => ['home8to10-relations', 'dont-invalidate-on-grade-change']),
  queryFn: () => findContents<BaseItem>({
    subtreeCriterion: [Subtree.App, Subtree.Content],
    contentIdCriterion: relationIds.value,
    mainLocationCriterion: true,
  }, relationIds.value.length),
  select: data => [...data]
    .sort((a, b) => relationIds.value.indexOf(a.contentId) - relationIds.value.indexOf(b.contentId)),
  staleTime: Infinity,
})

const teacherArticle = computed(() => (relations.value
  ?.find(({ contentId }) => teacherRelationId.value === contentId)) as TeacherArticle)
const funFact = computed(() => (relations.value
  ?.find(({ contentId }) => funFactIds.value.includes(contentId))) as ContentLink)
const sections = computed(() => (relations.value
  ?.filter(({ contentId }) => sectionIds.value.includes(contentId)) ?? []) as Array<ContentSectionStandalone>)

const imageRelationIds = computed(() => data?.value?.imageRelations?.destinationContentIds ?? [])
const imageRandomIds = computed(() => [imageRelationIds.value[Math.floor(randomSeed() * imageRelationIds.value.length)]])
const { data: image } = firstOf(useMedia(imageRandomIds))

const openTermExams = computed(() => tasks.value?.filter(isExamTask) ?? [])
const isLoading = computed(() => isLoadingTasks.value || isLoadingLocations.value || isLoadingHome.value)
const showTasks = computed(() =>
  !isTeacher.value
  && !isLoading.value
  && (enrichedTasks.value.length > 0 || openTermExams.value?.length > 0)
)

const prioritizedTasks = computed(() => {
  const examIds = new Set(openTermExams.value.map(e => e.taskId))
  const rest = enrichedTasks.value.filter(t => !examIds.has(t.taskId))

  return [...openTermExams.value, ...rest].slice(0, 3)
})
</script>

<template>
  <div class="flex h-full flex-col text-white">
    <h1
      class="sr-only"
      v-text="t('home.title')"
    />
    <DefaultMessages class="relative z-10 mt-24 -mb-18 w-full" />
    <div>
      <div class="border-green-20 relative items-center gap-y-3 border-b pb-16 sm:gap-x-3">
        <div class="max-w-screen-au mx-auto grid items-center gap-6 px-4 pt-24 sm:px-8 md:grid-cols-2">
          <div>
            <Greeting :article="teacherArticle" class="mb-4 w-fit lg:float-left lg:mr-4 lg:mb-0" />
            <HomeSubjects class="contents" />
          </div>
          <Home8To10Shared
            v-if="showTasks"
            :tasks="prioritizedTasks"
            class="z-20 md:ml-20 md:w-2/3"
          />
          <Home8To10FunFact
            v-else-if="funFact"
            :link="funFact"
            class="z-20 self-center md:ml-40"
          />
        </div>
        <Image
          v-if="image"
          :content="image"
          class="absolute top-0 right-0 bottom-0 hidden !h-full !w-1/2 object-cover [clip-path:polygon(8rem_0,100%_0,100%_100%,0_100%)] md:block"
          width="50vw"
        />
      </div>
      <BannerArticles
        :grade-type="GradeType.Grade8To10"
        :is-loading="isLoadingRelations"
        :sections="sections ?? []"
        class="max-w-screen-au mx-auto mt-16 px-4 sm:px-8"
      />
    </div>
  </div>
</template>
